import { Link } from 'react-router-dom';
import type { ComparisonSummary, Dashboard, RecommendationPage } from '@ederest/contracts';
import { Card } from '../../shared/ui/Card.js';
import { displayFieldLabel } from '../../shared/ui/field-label.js';
import { FrictionScore } from '../../shared/ui/FrictionScore.js';
import { ReadingList, type ReadingRow } from '../../shared/ui/ReadingList.js';
import { DirectionChip } from './DirectionChip.js';
import { COMPONENT_LABELS, formatIndicatorValue, isEffectMeasurable } from './comparison-format.js';
import './dashboard-summary.css';

interface DashboardSummaryProps {
  dashboard: Dashboard | undefined;
  latestComparison: ComparisonSummary | undefined;
  recommendations: RecommendationPage | undefined;
}

const DAY_MS = 86_400_000;

const count = (value: number) => value.toLocaleString('fr-FR');
const plural = (value: number, singular: string, pluralForm: string) => (value > 1 ? pluralForm : singular);
const percent = (value: number) => `${value.toFixed(1).replace('.', ',')} %`;
/** A label the page wrote in Arabic must not reorder the French sentence around it (first-strong isolate). */
const isolate = (text: string) => `⁨${text}⁩`;

/**
 * The dashboard read in plain words, from the figures the API returns and
 * nothing else: each line says one thing, shows the figure it rests on and
 * leads to the detail. Meant for someone who opens the Console for the first time.
 */
export function DashboardSummary({ dashboard, latestComparison, recommendations }: DashboardSummaryProps) {
  if (!dashboard) return null;

  const sessions = dashboard.kpis.find((kpi) => kpi.key === 'sessions')?.value ?? 0;
  const completion = dashboard.kpis.find((kpi) => kpi.key === 'completion_rate')?.value ?? 0;
  const hardest = dashboard.frictionPoints[0];
  const pending = recommendations?.pendingCount ?? 0;
  const days = Math.round((Date.parse(dashboard.period.to) - Date.parse(dashboard.period.from)) / DAY_MS);

  const rows: ReadingRow[] = [
    {
      label: 'Fréquentation',
      content:
        sessions === 0 ? (
          'Aucun usager mesuré sur la période choisie.'
        ) : (
          <>
            <strong>{count(sessions)}</strong> {plural(sessions, 'usager mesuré', 'usagers mesurés')} sur la
            période.
          </>
        ),
      visual: sessions > 0 && <CompletionBar completion={completion} />,
    },
  ];

  if (hardest) {
    const field = hardest.fieldKey ? displayFieldLabel(hardest.fieldLabel, hardest.fieldKey) : null;
    rows.push({
      label: 'Plus forte friction',
      content: (
        <>
          {isolate(hardest.stepName)}
          {field && <>, champ « {isolate(field)} »</>}
          <span className="reading-list__sub">
            {count(hardest.affectedSessions)}{' '}
            {plural(hardest.affectedSessions, 'usager concerné', 'usagers concernés')} · {hardest.journeyName}
          </span>
        </>
      ),
      visual: <FrictionScore score={hardest.frictionScore} />,
      action: { label: 'Voir le parcours', to: `/journeys/${hardest.journeyId}` },
    });
  }

  rows.push(effectRow(latestComparison));

  rows.push({
    label: 'À décider',
    content:
      pending === 0 ? (
        'Aucune recommandation en attente.'
      ) : (
        <>
          <strong>{count(pending)}</strong>{' '}
          {plural(pending, 'recommandation attend', 'recommandations attendent')} une décision de l'équipe.
        </>
      ),
    visual: pending > 0 && (
      <Link to="/recommendations" className="btn btn--primary btn--sm summary__cta">
        Ouvrir la file ({count(pending)})
      </Link>
    ),
  });

  return (
    <div data-testid="dashboard-summary">
      <Card
        title={
          <>
            En résumé <span className="summary__period">· {days} derniers jours</span>
          </>
        }
        className="dash__section"
      >
        <ReadingList rows={rows} />
      </Card>
    </div>
  );
}

/** Completion against abandon, in one bar, with the share in words under it. */
function CompletionBar({ completion }: { completion: number }) {
  const share = Math.max(0, Math.min(100, completion));
  return (
    <div className="summary__completion">
      <div className="summary__bar" role="img" aria-label={`${percent(share)} vont au bout`}>
        <span className="summary__bar-done" style={{ width: `${share}%` }} />
      </div>
      <div className="summary__bar-legend">
        <span>
          <strong>{percent(share)}</strong> vont au bout
        </span>
        <span>{percent(100 - share)} abandonnent</span>
      </div>
    </div>
  );
}

function effectRow(comparison: ComparisonSummary | undefined): ReadingRow {
  if (!comparison) {
    return { label: 'Effet mesuré', content: "Aucune guidance publiée pour l'instant." };
  }
  const published = new Date(comparison.publishedAt).toLocaleDateString('fr-FR');
  const action = { label: 'Voir la comparaison', to: '/comparison' };

  if (!isEffectMeasurable(comparison)) {
    return {
      label: 'Effet mesuré',
      content:
        comparison.guidances.length === 0 ? (
          <>
            La dernière version publiée n'ajoute aucune guidance.
            <span className="reading-list__sub">Pas d'effet à mesurer.</span>
          </>
        ) : (
          <>
            Effet pas encore mesurable.
            <span className="reading-list__sub">
              Trop peu de sessions de part et d'autre de la publication du {published}.
            </span>
          </>
        ),
      action,
    };
  }

  const [first] = comparison.guidances;
  const extra = comparison.guidances.length - 1;
  const delta = comparison.completionRate;
  return {
    label: 'Effet mesuré',
    content: (
      <>
        {first ? (COMPONENT_LABELS[first.component] ?? first.component) : 'Guidance'}
        {first?.stepName && <> sur « {isolate(first.stepName)} »</>}
        {extra > 0 && ` et ${extra} autre${extra > 1 ? 's' : ''}`}
        <span className="reading-list__sub">
          Publiée le {published} · taux d'achèvement, {comparison.days} jours avant et après
        </span>
      </>
    ),
    visual: (
      <span className="summary__effect">
        <span className="summary__effect-before">{formatIndicatorValue(delta.beforeValue, delta.unit)}</span>
        <span aria-hidden="true">→</span>
        <strong>{formatIndicatorValue(delta.afterValue, delta.unit)}</strong>
        <DirectionChip delta={delta} />
      </span>
    ),
    action,
  };
}
